import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Col, DatePicker, Form, Input, InputNumber, Modal, Radio, Row, Spin, Typography } from 'antd';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { getCustomerFinanceSummary, refundCustomer } from '@/redux/actions/receiptActions';
import { paymentMethodOptions, renderMoney } from '@/utils/helpers';

const makeKey = () => `customer-refund-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const displayText = (value, fallback = '—') => value == null || value === '' ? fallback : String(value);
const summaryCard = (label, value, highlighted = false) => <div style={{
    height: '100%', padding: isMobile ? '9px 10px' : '10px 12px', borderRadius: 6,
    border: highlighted ? '1px solid #91caff' : '1px solid #e8e8e8',
    background: highlighted ? '#e6f7ff' : '#fafafa',
}}>
    <div style={{ color: 'rgba(0, 0, 0, 0.55)', fontSize: 12, lineHeight: '18px', marginBottom: 2 }}>{label}</div>
    <div style={{
        color: highlighted ? '#096dd9' : 'rgba(0, 0, 0, 0.88)', fontSize: highlighted ? 20 : 17,
        fontWeight: 700, lineHeight: '25px', textAlign: 'right', whiteSpace: 'nowrap'
    }}>{renderMoney(value)}</div>
</div>;
/** Modal dùng chung để hoàn tiền theo khách hàng hoặc giới hạn đúng nguồn của một phiếu thu. */
export default function CustomerRefundModal({ visible, customerId, receiptId, receiptCode, onCancel, onCompleted }) {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [data, setData] = useState({ refundable_amount: 0 });
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const refundRequestKey = useRef('');
    const receiptScope = !!receiptId;
    const totalRefundable = useMemo(() => Math.max(0, Number(data.refundable_amount || 0)), [data.refundable_amount]);

    const load = useCallback(() => {
        if (!visible || !customerId) return Promise.resolve();
        setLoading(true);
        return dispatch(getCustomerFinanceSummary(customerId, receiptId || null))
            .then(setData)
            .finally(() => setLoading(false));
    }, [dispatch, visible, customerId, receiptId]);

    useEffect(() => {
        if (!visible) return;
        form.resetFields();
        setData({ refundable_amount: 0 });
        refundRequestKey.current = makeKey();
        form.setFieldsValue({ refund_at: moment(), idempotency_key: refundRequestKey.current });
        load().catch(() => null);
    }, [visible, form, load]);

    const selectAllSources = () => form.setFieldsValue({ amount: totalRefundable });

    const submit = () => form.validateFields().then((values) => {
        setSubmitting(true);
        return dispatch(refundCustomer({
            customer_id: Number(customerId),
            receipt_id: receiptScope ? Number(receiptId) : null,
            refund_scope: receiptScope ? 'RECEIPT' : 'CUSTOMER',
            amount: Number(values.amount),
            payment_method: String(values.payment_method || '').trim(),
            refund_at: values.refund_at.format('YYYY-MM-DD HH:mm:ss'),
            reason: String(values.reason || '').trim(),
            idempotency_key: refundRequestKey.current,
        })).then(async () => {
            await load();
            if (onCompleted) await onCompleted();
            onCancel();
        }).finally(() => setSubmitting(false));
    });

    return <Modal
        visible={visible}
        destroyOnClose
        width={isMobile ? '100%' : '60%'}
        title={receiptScope ? `Hoàn tiền phiếu ${displayText(receiptCode || data.refund_receipt?.code)}` : 'Hoàn tiền cho khách'}
        okText="Xác nhận hoàn tiền"
        cancelText="Hủy"
        confirmLoading={submitting}
        onOk={submit}
        onCancel={onCancel}
        maskClosable={false}
    >
        <Spin spinning={loading}>
            <Alert
                type="warning"
                message={receiptScope
                    // [LOCKED-PERIOD-REFUND] Làm rõ: kỳ hoa hồng khóa không chặn hoàn tiền; kết quả gốc vẫn bất biến.
                    ? 'Hệ thống tự hoàn đúng các nguồn còn khả dụng của phiếu. Phiếu gốc và lịch sử tại thời điểm thu được giữ nguyên. Nếu kỳ hoa hồng nguồn đã khóa, phần ảnh hưởng hoa hồng sẽ được ghi nhận vào kỳ mở tiếp theo, không sửa kết quả đã khóa.'
                    : 'Hệ thống tự xác định nguồn tiền và phân bổ hoàn tiền theo dữ liệu thực tế của khách hàng. Nếu kỳ hoa hồng nguồn đã khóa, phần ảnh hưởng hoa hồng sẽ được ghi nhận vào kỳ mở tiếp theo, không sửa kết quả đã khóa.'}
                style={{ marginBottom: 16 }}
            />
            <Form form={form} layout="vertical">
                <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
                        <Col xs={12} md={8}>{summaryCard('Tổng có thể hoàn',
                            totalRefundable, true)}</Col>
                        <Col xs={12} md={8}>{summaryCard('Đang chọn hoàn', Number(getFieldValue('amount') || 0), true)}</Col>
                        <Col xs={12} md={8}>{summaryCard('Đã hoàn cho khách', data.refunded_amount)}</Col>
                    </Row>}
                </Form.Item>
                <Button onClick={selectAllSources} disabled={totalRefundable <= 0}
                    style={{ marginBottom: 16 }}>Hoàn toàn bộ phần còn lại</Button>
                <Form.Item name="amount" label="Số tiền hoàn" rules={[
                    { required: true, message: 'Vui lòng nhập số tiền hoàn.' },
                    {
                        validator: (_, value) => {
                            return Number(value) > 0 && Number(value) <= totalRefundable
                                ? Promise.resolve() : Promise.reject(new Error('Số tiền hoàn phải lớn hơn 0 và không vượt tổng có thể hoàn.'));
                        }
                    },
                ]}>
                    <InputNumber min={1} precision={0} style={{ width: '100%' }} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />
                </Form.Item>
                <Form.Item name="payment_method" label="Hình thức hoàn" rules={[{ required: true, message: 'Vui lòng chọn hình thức hoàn tiền.' }]}>
                    <Radio.Group options={(paymentMethodOptions || []).map(item => ({ value: item.value, label: item.label }))} />
                </Form.Item>
                <Form.Item name="refund_at" label="Ngày hoàn" rules={[
                    { required: true, message: 'Vui lòng chọn ngày hoàn.' },
                    { validator: (_, value) => value && value.isValid() ? Promise.resolve() : Promise.reject(new Error('Ngày hoàn không hợp lệ.')) },
                ]}>
                    <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="reason" label="Lý do" rules={[{ required: false, whitespace: true, message: 'Vui lòng nhập lý do hoàn tiền.' }]}>
                    <Input.TextArea rows={3} maxLength={1000} />
                </Form.Item>
                <Form.Item name="idempotency_key" hidden rules={[{ required: true }]}><Input /></Form.Item>
            </Form>
            {totalRefundable <= 0
                && <Typography.Text type="secondary">Phiếu/khách hàng không còn nguồn có thể hoàn.</Typography.Text>}
        </Spin>
    </Modal>;
}
